import { CircularProgress, createStyles, makeStyles } from '@material-ui/core';
import { useKBar } from 'kbar';
import React, { useContext } from 'react';
import { z } from 'zod';

import { PERM_MOD_ANALYTICS } from 'src/constants';
import { SETTINGS_PAGE_MAX_WIDTH } from 'src/constants/uiConsts';
import { RouteContext } from 'src/context';
import history from 'src/history';
import { useCustomerSubscription } from 'src/hooks/useCustomerSubscription';
import { usePrefill } from 'src/hooks/usePrefill';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import {
  AnalyticsSection,
  RecentlyVisited,
} from 'src/legacy/components/HomePage';
import { FactOfTheDaySectionHeader } from 'src/legacy/components/HomePage/OnboardingTasksSection/SectionHeader';
import { ResourcesSection } from 'src/legacy/components/HomePage/ResourcesSection';
import { UserActionItems } from 'src/legacy/components/HomePage/UserActionItems';
import { CheckIcon } from 'src/legacy/components/Icons';
import StandardPage from 'src/legacy/components/UI/StandardPage';
import { useGetAllRecommendedResourcesQuery } from 'src/services/api';
import { useGetOnboardingTaskStatusQuery } from 'src/services/api/improvedOnboardingApi';
import { shortcutsSidebarOpen } from 'src/store/shortcuts/slice';
import { selectCurrentUser } from 'src/store/storeUtils';
import { toggleHelpCenterModalAction } from 'src/store/ui/actions';
import { hasPermission } from 'src/utils/UserUtils';

import { OnboardingTasksSection } from './OnboardingTasksSection/OnboardingTasksSection';

const useStyles = makeStyles(() =>
  createStyles({
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      margin: '0 auto',
      height: '100%',
    },
    root: {
      paddingTop: 40,
      maxWidth: SETTINGS_PAGE_MAX_WIDTH,
      width: '100%',
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      // this should add a vertical spacing between
      //  sections setup guide and latest release
      gap: 40,
      paddingBottom: '100px',
    },
  }),
);

enum ResourceAction {
  openShortcut = 'shortcuts',
  contactSupport = 'contactSupport',
  openCommandbar = 'openCommandbar',
}

// Schema for homepage prefilled data
const HomePagePrefillSchema = z
  .object({
    // Add any specific fields that might be passed to the homepage
    // For now, we'll keep it flexible with additional properties
  })
  .passthrough();

export const HomePageContent = () => {
  const { query } = useContext(RouteContext);
  const { pageAction } = query;
  const { action } = usePrefill(HomePagePrefillSchema);
  const currentUser = useAppSelector(selectCurrentUser);
  const currentUserPermissions = useAppSelector(
    (state) => state.user.data?.permissions,
  );

  const { allSubscriptions } = useCustomerSubscription();

  const {
    isLoading: isRecommendedResourcesLoading,
    data: recommendedResources,
  } = useGetAllRecommendedResourcesQuery();

  const {
    data: onboardingTaskStatus,
    isLoading: isOnboardingTaskStatusLoading,
    error: onboardingTaskStatusError,
  } = useGetOnboardingTaskStatusQuery({ subscriptions: allSubscriptions });

  const classes = useStyles();
  const dispatch = useAppDispatch();

  /**
   * This function resets the query search params
   */
  const resetQuerySearch = () => {
    history.replace({
      search: '',
    });
  };

  const { query: commandBarQuery } = useKBar();
  const hasAnalyticsPermission = React.useMemo(
    () => hasPermission(PERM_MOD_ANALYTICS, currentUserPermissions),
    [currentUserPermissions],
  );
  const handleResourceCardActions = React.useCallback(() => {
    // Use action from usePrefill if available, otherwise fall back to pageAction
    const currentAction = action || pageAction;

    switch (currentAction) {
      case ResourceAction.openShortcut:
        dispatch(shortcutsSidebarOpen(true));
        break;

      case ResourceAction.contactSupport:
        dispatch(toggleHelpCenterModalAction(true));
        break;

      case ResourceAction.openCommandbar:
        commandBarQuery.toggle();
        break;

      default:
        break;
    }
  }, [action, pageAction, dispatch, commandBarQuery]);

  React.useEffect(() => {
    handleResourceCardActions();
    // reset pageAction query param after resource card is handled
    if (pageAction || action) resetQuerySearch();
  }, [pageAction, action, handleResourceCardActions]);

  const showRecommendedResourcesSection = Boolean(
    recommendedResources &&
      recommendedResources?.structFields?.length &&
      !isRecommendedResourcesLoading,
  );

  const allResourcesLoaded =
    (!isOnboardingTaskStatusLoading || onboardingTaskStatusError) &&
    !isRecommendedResourcesLoading &&
    currentUser !== undefined;

  if (!allResourcesLoaded) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <StandardPage
      title={`That's it for now`}
      topIcon={<CheckIcon />}
      desc="Check back later for more insights and advice about your business."
      showContent={true}
    >
      <div className={classes.root}>
        <FactOfTheDaySectionHeader />
        {onboardingTaskStatus && (
          <OnboardingTasksSection onboardingStatus={onboardingTaskStatus} />
        )}
        {hasAnalyticsPermission && <AnalyticsSection />}
        <UserActionItems />
        <RecentlyVisited />
        {showRecommendedResourcesSection && (
          <ResourcesSection
            resources={recommendedResources?.structFields || []}
          />
        )}
      </div>
    </StandardPage>
  );
};
